<script setup>
import { computed, reactive, ref, shallowRef, watch } from 'vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import PageTabs from '@/components/Utils/PageTabs.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useToast } from '@/composables/useToast';
import { required } from '@/utils/validation';

// Add or edit a page (or menu group) in the catalog roles pick from.
const props = defineProps({
  page: { type: Object, default: null }, // null = add
  groups: { type: Array, default: () => [] }, // pages with isParent
});
const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(['saved']);

const TYPES = [
  { id: 'page', name: 'Page' },
  { id: 'group', name: 'Group' },
];

const toast = useToast();
const formRef = ref(null);
const saving = shallowRef(false);
const form = reactive({ type: 'page', title: '', icon: '', route: '', parentID: null });

const isGroup = computed(() => form.type === 'group');
const routeRule = (v) =>
  isGroup.value || /^\/[a-z0-9/_-]*$/i.test(v || '') || 'Start with / and use letters, numbers, - or _.';
const iconPreview = computed(() =>
  /^mdi-[a-z0-9-]+$/.test(form.icon) ? form.icon : 'mdi-help-box-outline',
);

watch(open, (isOpen) => {
  if (!isOpen) return;
  const p = props.page;
  form.type = Number(p?.isParent) === 1 ? 'group' : 'page';
  form.title = p?.title ?? '';
  form.icon = p?.icon ?? '';
  form.route = p?.route ?? '';
  form.parentID = p?.parentID != null && p.parentID !== '' ? Number(p.parentID) : null;
  formRef.value?.resetValidation();
});

async function save() {
  const { valid } = await formRef.value.validate();
  if (!valid) return;
  saving.value = true;
  try {
    const data = {
      title: form.title.trim(),
      icon: form.icon.trim(),
      isParent: isGroup.value,
      route: isGroup.value ? null : form.route.trim(),
      parentID: isGroup.value ? null : form.parentID,
    };
    const body = props.page
      ? await apiRequest('patch', `/sys-modules/${props.page.id}`, data)
      : await apiRequest('post', '/sys-modules', data);
    assertOk(body);
    toast.success(props.page ? 'Page updated.' : 'Page added.');
    open.value = false;
    emit('saved');
  } catch (e) {
    toast.error(errorMessage(e, 'Could not save the page.'));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <UiDialog
    v-model="open"
    :title="page ? 'Edit page' : 'Add page'"
    description="Pages and groups that roles can add to their sidebar menu."
    size="md"
  >
    <v-form ref="formRef" class="form-stack" @submit.prevent="save">
      <UiFormField label="Type">
        <div><PageTabs v-model="form.type" :items="TYPES" /></div>
      </UiFormField>

      <UiFormField label="Title" required v-slot="{ id }">
        <v-text-field :id="id" v-model="form.title" :rules="[required]" hide-details="auto" />
      </UiFormField>

      <UiFormField
        label="Icon"
        description="A Material Design Icons name, e.g. mdi-account-outline."
        required
        v-slot="{ id }"
      >
        <v-text-field
          :id="id"
          v-model="form.icon"
          :prepend-inner-icon="iconPreview"
          placeholder="mdi-…"
          :rules="[required]"
          hide-details="auto"
        />
      </UiFormField>

      <template v-if="!isGroup">
        <UiFormField
          label="Address"
          description="The path after the role prefix, e.g. /faculty-list."
          required
          v-slot="{ id }"
        >
          <v-text-field
            :id="id"
            v-model="form.route"
            placeholder="/page-name"
            :rules="[required, routeRule]"
            hide-details="auto"
          />
        </UiFormField>

        <UiFormField
          label="Catalog group"
          description="Optional. Only organizes this list; each role's menu decides where the page appears."
          v-slot="{ id }"
        >
          <v-select
            :id="id"
            v-model="form.parentID"
            :items="groups"
            item-title="title"
            item-value="id"
            placeholder="None"
            clearable
            hide-details="auto"
          />
        </UiFormField>
      </template>
    </v-form>

    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Cancel</v-btn>
      <v-btn color="primary" :loading="saving" @click="save">
        {{ page ? 'Save changes' : 'Add page' }}
      </v-btn>
    </template>
  </UiDialog>
</template>
